import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../services/api';
import { parseEventDate } from '../utils/dateUtils';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        );

        const eventsData = await Promise.race([
          eventsAPI.getAll(),
          timeout
        ]);

        const upcomingEvents = (Array.isArray(eventsData) ? eventsData : eventsData?.content || [])
          .filter(event => {
            const dt = parseEventDate(event.startDate || event.startTime);
            return dt && dt >= new Date();
          })
          .sort((a, b) => {
            const da = parseEventDate(a.startDate || a.startTime);
            const db = parseEventDate(b.startDate || b.startTime);
            return (da?.getTime() || 0) - (db?.getTime() || 0);
          });

        setEvents(upcomingEvents);
      } catch (error) {
        console.warn('Backend not available, using fallback data:', error);
        setEvents(getFallbackEvents());
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const highlightEvent = useMemo(() => (!loading && events.length > 0 ? events[0] : null), [events, loading]);
  const secondaryEvents = useMemo(() => (!loading ? events.slice(1, 5) : []), [events, loading]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    const destination = query ? `/events?search=${encodeURIComponent(query)}` : '/events';
    navigate(destination);
  };

  const getEventImage = (event) =>
    event?.imageUrl || event?.coverImage || event?.heroImage || event?.bannerImage || '/placeholder.png';

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const getFallbackEvents = () => [
    {
      id: 1,
      name: 'Summer Music Festival 2025',
      description: 'The biggest music festival of the year featuring top artists from around the world',
      category: 'CONCERT',
      startDate: '2025-07-15T18:00:00',
      venue: { name: 'Central Park Arena' }
    },
    {
      id: 2,
      name: 'Broadway Classics Night',
      description: 'Experience the magic of Broadway with classic performances',
      category: 'THEATER',
      startDate: '2025-08-22T19:00:00',
      venue: { name: 'Grand Theater' }
    },
    {
      id: 3,
      name: 'Championship Finals',
      description: 'Watch the most anticipated sports event of the season',
      category: 'SPORTS',
      startDate: '2025-09-10T17:00:00',
      venue: { name: 'National Stadium' }
    },
    {
      id: 4,
      name: 'Jazz Under The Stars',
      description: 'An intimate evening with world-renowned jazz musicians',
      category: 'CONCERT',
      startDate: '2025-07-28T20:00:00',
      venue: { name: 'Riverside Jazz Club' }
    }
  ];

  /* Helper to get category icons as SVGs */
  const getCategoryIcon = (category) => {
    // Simple mapping to generic icons or specific paths if needed
    // For now, we can return null to keep it cleaner, or specific SVGs
    // Let's use a generic 'ticket' or 'event' icon for all, or distinct ones.
    // Keeping it minimal:
    switch (category) {
      case 'CONCERT':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
          </svg>
        );
      case 'SPORTS':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><path d="M5.5 5.5l13 13" /><path d="M18.5 5.5l-13 13" />
          </svg>
        );
      default:
        // Default star/ticket icon
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
        );
    }
  };

  const formatHighlightTime = (dateString) => {
    const date = parseEventDate(dateString);
    if (!date || isNaN(date.getTime())) {
      return 'Time TBA';
    }
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getEventName = (event) => event?.title || event?.name || 'Untitled Event';

  return (
    <div className="home-shell">
      <Helmet>
        <title>TikPlat - Book Thrilling Nights in Seconds</title>
        <meta
          name="description"
          content="Discover concerts, theater, sports, and festivals with cinematic seat maps and instant checkout."
        />
      </Helmet>

      <section className="home-hero-modern">
        <div className="home-hero-glow-bg" aria-hidden="true" />

        <div className="home-max home-hero-content">
          <div className="home-hero-main-col">
            <div className="home-hero-badge">
              The Future of Ticketing
            </div>
            <h1 className="home-hero-title-modern">
              <span className="block-reveal">Experience</span>
              <span className="gradient-text">The Extraordinary.</span>
            </h1>
            <p className="home-hero-subtitle-modern">
              Secure the best seats for the most anticipated concerts, sports, and theater events worldwide.
            </p>

            <form className="home-search-modern" onSubmit={handleSearchSubmit}>
              <div className="home-search-glass">
                <span className="search-icon-modern">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="What's your next memory?"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <button type="submit" className="search-btn-modern">Search</button>
              </div>
            </form>

            <div className="hero-metrics-modern">
              <div className="metric-pill">
                <strong>10M+</strong> <span>Tickets Sold</span>
              </div>
              <div className="metric-pill">
                <strong>100%</strong> <span>Verified</span>
              </div>
            </div>
          </div>

          <div className="home-hero-visual-col">
            {highlightEvent && (
              <div className="glass-card-floating">
                <div className="glass-card-inner">
                  <div className="glass-card-image-wrapper">
                    <img
                      src={getEventImage(highlightEvent)}
                      alt={getEventName(highlightEvent)}
                    />
                    <div className="glass-card-overlay" />
                    <div className="glass-date-badge">
                      <div className="month">{new Date(highlightEvent.startDate || highlightEvent.startTime).toLocaleDateString('en-US', { month: 'short' })}</div>
                      <div className="day">{new Date(highlightEvent.startDate || highlightEvent.startTime).getDate()}</div>
                    </div>
                  </div>

                  <div className="glass-card-content">
                    <div className="glass-card-header">
                      <span className="glass-category-tag">{highlightEvent.category || 'Event'}</span>
                      <span className="glass-status-tag">Selling Fast</span>
                    </div>

                    <h2 className="glass-card-title">{getEventName(highlightEvent)}</h2>

                    <div className="glass-card-details">
                      <div className="glass-detail-row">
                        <span className="icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                        </span>
                        {highlightEvent.venueName || highlightEvent.venue?.name || 'Venue TBA'}
                      </div>
                      <div className="glass-detail-row">
                        <span className="icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                        </span>
                        {formatHighlightTime(highlightEvent.startDate || highlightEvent.startTime)}
                      </div>
                    </div>

                    <button
                      className="glass-card-cta"
                      onClick={() => handleEventClick(highlightEvent.id)}
                    >
                      Get Tickets
                      <span className="arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                      </span>
                    </button>
                  </div>
                </div>

                {/* Decorative floating elements behind */}
                <div className="floating-orb orb-1" />
                <div className="floating-orb orb-2" />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="home-events" id="events">
        <div className="home-max">
          <div className="home-section-header">
            <div>
              <h2>Unmissable events</h2>
              <p>Handpicked shows, talks, and games that beat endless scrolling.</p>
            </div>
            <Link to="/events" className="home-section-cta">
              View all events
            </Link>
          </div>

          {loading ? (
            <div className="home-loading">
              <div className="home-loading-spinner" />
              <p>Fetching unforgettable nights…</p>
            </div>
          ) : events.length === 0 ? (
            <div className="home-empty">
              <div className="home-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              </div>
              <h3>No upcoming events</h3>
              <p>New drops land daily. Check back soon for the next can&apos;t-miss night.</p>
            </div>
          ) : (
            <div className="home-events-grid">
              {(secondaryEvents.length > 0 ? secondaryEvents : events).map((event) => {
                const startDateTime = event.startDate || event.startTime;
                const eventDate = startDateTime ? parseEventDate(startDateTime) : null;
                const month = eventDate ? eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : 'TBA';
                const day = eventDate ? eventDate.getDate() : '--';
                const timeLabel = formatHighlightTime(startDateTime);
                const locationLabel = event.venueName || event.venue?.name || 'Venue to be announced';
                const categoryLabel = event.category || 'Live Experience';

                return (
                  <article
                    key={event.id}
                    className="home-event-card"
                    onClick={() => handleEventClick(event.id)}
                    onKeyDown={(evt) => {
                      if (evt.key === 'Enter') handleEventClick(event.id);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="home-event-hero"
                      style={{ backgroundImage: `url(${getEventImage(event)})` }}
                    >
                      <div className="home-hero-badges">
                        {event.isFeatured && <div className="home-hero-pill featured">Featured</div>}
                        <div className="home-hero-pill glass">
                          {getCategoryIcon(event.category)} {categoryLabel}
                        </div>
                      </div>
                    </div>

                    <div className="home-event-body">
                      <div className="home-event-date-row">
                        <span className="event-date-accent">{month} {day}</span>
                        <span className="event-dot">•</span>
                        <span className="event-time">{timeLabel}</span>
                      </div>

                      <h3 className="home-event-title">{getEventName(event)}</h3>

                      <div className="home-event-venue">
                        <span className="icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                        </span> {locationLabel}
                      </div>

                      <p className="home-event-description">
                        {event.description
                          ? (event.description.length > 80 ? event.description.substring(0, 80) + '...' : event.description)
                          : 'Join us for an unforgettable experience.'}
                      </p>

                      <button
                        type="button"
                        className="home-event-cta"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          handleEventClick(event.id);
                        }}
                      >
                        Get Tickets
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="home-categories">
        <div className="home-max">
          <div className="home-section-header">
            <div>
              <h2>Browse by Category</h2>
              <p>Find your vibe.</p>
            </div>
          </div>
          <div className="home-category-grid">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                className="home-category-card"
                onClick={() => navigate(`/events?category=${cat.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/events?category=${cat.id}`)}
              >
                <div className="category-icon">{cat.icon}</div>
                <span className="category-name">{cat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const CATEGORIES = [
  {
    id: 'Concert',
    label: 'Concerts',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
    )
  },
  {
    id: 'Sports',
    label: 'Sports',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M5.5 5.5l13 13" /><path d="M18.5 5.5l-13 13" /></svg>
    )
  },
  {
    id: 'Theater',
    label: 'Theater',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 10s5-3 10-3 10 3 10 3v11L12 18l-10 3z" /><line x1="12" y1="7" x2="12" y2="22" /></svg>
    )
  },
  {
    id: 'Comedy',
    label: 'Comedy',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 14s.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
    )
  },
  {
    id: 'Exhibition',
    label: 'Exhibitions',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" /><path d="M21 15l-5-5L5 21" /></svg>
    )
  },
  {
    id: 'Festival',
    label: 'Festivals',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /></svg>
    )
  }
];

export default HomePage;
